import React, { StyleHTMLAttributes } from "react";
import * as Yup from 'yup'
import { FormikProps } from "formik";
import TextInputField, { TextInputObj as TextInput } from "app/partials/form/TextInputField";
import ButtonAction, { ButtonActionObj } from "app/partials/actions/ButtonAction";
import DateRangeInputField, { DateRangeInputObj as DatetimeRangeInput } from "app/partials/form/DatetimeRangeField";
import DateTimePickerField, { DateTimePickerInputObj as DatetimePickerInput } from "app/partials/form/DateTimePicker";
import SelectInputField, { SelectInput } from "app/partials/form/SelectInputField";
import AreaInputField, { AreaInput } from "app/partials/form/AreaInputField";
import CheckboxInputField, { CheckboxInput } from "app/partials/form/CheckboxInputField";
import FileBase64InputField, { FileBase64Input } from "app/partials/form/FileBase64InputField";
import MultiplyField, { appendParentToKeys } from "app/partials/form/MultiplyField";
import { Col, Row, } from "react-bootstrap";
import MultiTextView, { MultiTextViewObj } from "app/partials/view/MultiTextView";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import TitleView, { TitleViewObj } from "app/partials/view/TitleView";
import { Entity } from "app/models/entity";
import WysiswygAreaInputField, { WysiswygAreaInputObj } from "app/partials/form/WysiwygAreaField";
import { StylesConfig } from "react-select";
import { Actions } from "../components/Modal/ModalEntity";
import AbsencesRangeInputField, {AbsencesRangeInputObj as AbsencesRangeInput} from "../../../partials/form/AbsencesRangeField";

export interface Multiple {
    multiple?: boolean
    addLabel?: string | JSX.Element
    removeLabel?: string | JSX.Element
}

export interface DependsOn {
    dependsOn?: string
}

export interface LinkedTo<T> {
    linkedTo?: keyof T
}

export interface ColumnOrder {
    columnNo?: number
    title?: string
}

export interface Label {
    label?: string
}

export interface AdditionalLabel {
    duplicateLabel?: string
}

export interface Type {
    type: string
}

export interface ConditionalShow<T> {
    showIf?: (entity: T) => Promise<boolean>
}

export interface Attributes {
    attributes?: {
        [key: string]: string | number | boolean | Object
    }
}

export interface Validation {
    validation?: Yup.AnySchema
}

export interface SubModel {
    // TODO: use extension of Entity instead of any
    valueRender?: (entity: any) => string
}

export type CustomField<T> = {
    type: "custom",
    key: keyof T,
    render: (formik: FormikProps<T>, entity?: T, field?: FormField<T>, actions?: Actions<T>) => JSX.Element
}

export type InputTypes<T> = (TextInput<T> | WysiswygAreaInputObj<T> | AreaInput<T> | SelectInput<T> | CheckboxInput<T> | FileBase64Input<T> | DatetimeRangeInput<T> | DatetimePickerInput<T> | AbsencesRangeInput<T>) & Attributes & Validation & Type & Label & SubModel & ConditionalShow<T> & LinkedTo<T>

export type ViewTypes<T> = (MultiTextViewObj<T> | TitleViewObj<T>) & Attributes & Validation & Type & Label & SubModel & ColumnOrder & ConditionalShow<T>

export type ActionTypes<T> = (ButtonActionObj<T>) & Attributes & SubModel & ColumnOrder & ConditionalShow<T>

export type FormBuilderTypes<X extends Entity> = {
    [key: string]: any,
    type: "formbuilder",
    key: keyof any,
    form: FormFields<X>
}

export type FormField<T> = (InputTypes<T> | ViewTypes<T> | ActionTypes<T> | CustomField<T> | FormBuilderTypes<any>) & Attributes & Validation & Type & Label & AdditionalLabel & SubModel & ColumnOrder & Multiple & DependsOn

export type FormFields<T> = {
    [key: string]: FormField<T>
}

export interface TabsConfig {
    tabName?: string,
    hasTabs?: boolean
}

export type TabObj<T> = {
    name: string,
    fields: FormFields<T>,
}

export type FormFieldsTabs<T> = {
    tabs: TabObj<T>[],
    hasTabs: true
    defaultActiveTab?: number
}

export type FormProps<T extends Entity> = {
    formik: FormikProps<T>,
    entity: T,
    actions: Actions<T>,
    onChange?: (e: T) => void,
}

const isInputField = (type: string) => {
    return ['text', 'checkbox', 'select', 'textarea', 'wysiwyg', 'file_base64', 'daterange', 'timerange', 'absencesdaterange', 'absencestimerange', 'datetime_picker', 'date_picker', 'time_picker'].includes(type)
}

const isViewField = (type: string) => {
    return ['multiTextView', 'titleView'].includes(type)
}

const isActionField = (type: string) => {
    return ['buttonAction'].includes(type)
}

const createInput = <T,>(formik: FormikProps<T>, inputObj: InputTypes<T>, _: T, actions: Actions<T>): JSX.Element => {
    switch (inputObj.type) {
        case "datetime_picker":
        case "date_picker":
        case "time_picker":
            return <DateTimePickerField<T> formik={formik} inputObj={inputObj} actions={actions} />
        case "daterange":
        case "timerange":
            return <DateRangeInputField<T> formik={formik} inputObj={inputObj} actions={actions} />
        case "absencesdaterange":
        case "absencestimerange":
            return <AbsencesRangeInputField<T> formik={formik} inputObj={inputObj} actions={actions} />
        case "text":
            return <TextInputField<T> formik={formik} inputObj={inputObj} actions={actions}/>
        case "wysiwyg":
            return <WysiswygAreaInputField<T> formik={formik} inputObj={inputObj} actions={actions} />
        case "textarea":
            return <AreaInputField<T> formik={formik} inputObj={inputObj} actions={actions} />
        case "select":
            return <SelectInputField<T> formik={formik} inputObj={inputObj} actions={actions} />
        case "checkbox":
            return <CheckboxInputField<T> formik={formik} inputObj={inputObj} actions={actions} />
        case "file_base64":
            return <FileBase64InputField<T> formik={formik} inputObj={inputObj} actions={actions} />
        default:
            return <></>
    }
}

const createView = <T extends Entity,>(formik: FormikProps<T>, inputObj: ViewTypes<T>, entity: T, actions: Actions<T>): JSX.Element => {
    switch (inputObj.type) {
        case "multiTextView":
            return <MultiTextView<T> formik={formik} inputObj={inputObj} entity={entity} />
        case "titleView":
            return <TitleView<T> formik={formik} inputObj={inputObj} entity={entity} />
        default:
            console.log('Unknown view type: ', (inputObj as ViewTypes<T>).type)
            return <></>
    }
}

const createAction = <T,>(formik: FormikProps<T>, inputObj: ActionTypes<T>, entity: T, actions: Actions<T>): JSX.Element => {
    switch (inputObj.type) {
        case "buttonAction":
            return <ButtonAction<T> formik={formik} inputObj={inputObj} entity={entity} />
        default:
            console.log('Unknown action type: ', (inputObj as ActionTypes<T>).type)
            return <></>
    }
}

const createField = <T extends Entity, X extends Entity>(formik: FormikProps<T>, field: FormField<T>, entity: T, actions: Actions<T>) => {
    let component: JSX.Element = <></>;
    if (isInputField(field.type)) {
        component = createInput<T>(formik, field as InputTypes<T>, entity, actions);
    }
    if (isViewField(field.type)) {
        component = createView<T>(formik, field as ViewTypes<T>, entity, actions);
    }
    if (isActionField(field.type)) {
        component = createAction<T>(formik, field as ActionTypes<T>, entity, actions);
    }
    if (field.type === 'custom') {
        component = (field as CustomField<T>).render(formik, entity, field, actions);
    }
    if (field.type === 'formbuilder') {
        if (field.multiple) {
            return component =
                <MultiplyField<T, X>
                    formik={formik}
                    inputObj={field}
                    fieldBuilder={(formFields) => FormBuilder<T>({ formFields }).Component({ formik, entity, actions })}
                    entity={entity}
                    actions={actions}
                    isSubForm
                />
        }
        const parentEntityKey = field.key
        field.form = appendParentToKeys<X>(parentEntityKey as string, (field as FormBuilderTypes<X>).form)
        component = <> {FormBuilder<T>({ formFields: field.form }).Component({ formik, entity, actions })}</>
    }
    if (field.multiple) {
        component = <MultiplyField<T, T> actions={actions} formik={formik} inputObj={field} fieldBuilder={(field) => createField(formik, field, entity, actions)} entity={entity} />
    }
    return component;
}

type CreateFormProps<T extends Entity> = { formik: FormikProps<T>, fields: FormFields<T>, entity: T, tabName?: string, hasTabs?: boolean, actions: Actions<T> } & TabsConfig

const createSingleFormWrapper = <T extends Entity,>(formArray: Array<Array<JSX.Element>>, _: CreateFormProps<T>): JSX.Element => (
    <>{
        formArray.map((rows, index) => (
            <Col className="tp-form-column" key={index}>
                {rows}
            </Col>
        ))
    }</>
)

const createSingleFormArray = <T extends Entity, X extends Entity>(props: CreateFormProps<T>): Array<Array<JSX.Element>> => {
    const { formik, fields, entity, actions } = props;
    let formArray: Array<Array<JSX.Element>> = [[]];
    let fArrayIndex = 0;

    Object.keys(fields)
        .sort((a, b) => (fields[a].columnNo || 0) - (fields[b].columnNo || 0))
        .forEach((key, index, keys) => {
            const field = fields[key];
            let element = (
                <div className='fv-row mb-7' key={key}>
                    {createField<T, X>(formik, field, entity, actions)} </div>
            )
            if (index > 0 && fields[key].columnNo !== fields[keys[index - 1]].columnNo) {
                formArray[++fArrayIndex] = [];
            }
            formArray[fArrayIndex].push(element);
        })
    return formArray;
}

const CreateForm = <T extends Entity,>(props: CreateFormProps<T>): React.ReactElement => {
    const rows = (() => {
        let formElements = null
        formElements = createSingleFormWrapper(createSingleFormArray(props), props);

        return <>
            {formElements}
        </>
    })();

    return (rows);
}

export const createYupSchemaFromFormFields = <T,>(formFields: FormFields<T>) => {
    const yupSchema: any = {}
    Object.values(formFields).forEach((formField) => {
        const field = formField as FormField<T>
        if (field.validation) {
            yupSchema[field.key] = field.validation
        }
    })
    return Yup.object().shape(yupSchema)
}

type FormBuilderProps<T extends Entity> = {
    formFields: FormFields<T> | FormFieldsTabs<T>,
}

export type EntityForm<T extends Entity> = {
    validationRules: Yup.ObjectSchema<any>,
    Component: React.FC<FormProps<T>>,
}


const flattenFieldsValidation = <T extends Entity,>(f: FormFields<T> | FormFieldsTabs<T>): FormFields<T> => {
    if (f?.hasTabs) {
        return (f as FormFieldsTabs<T>).tabs.reduce((acc, tab) => (
            { ...acc, ...tab.fields }
        ), {})
    }

    return (f as FormFields<T>)
}

const FormBuilder = <T extends Entity,>({ formFields }: FormBuilderProps<T>): EntityForm<T> => {
    return {

        Component: ({ formik, entity, onChange = (e) => { }, actions }: FormProps<T>) => {
            return <>
                {
                    formFields?.hasTabs ?
                        <Tabs
                            defaultIndex={(formFields?.defaultActiveTab ?? 0) as number}
                        >
                            <TabList>
                                {(formFields as FormFieldsTabs<T>).tabs.map((tab: TabObj<T>, index: number) =>
                                    <Tab key={index}>{tab?.name ?? `Tab ${index}`}</Tab>
                                )}
                            </TabList>

                            {(formFields as FormFieldsTabs<T>).tabs.map((tab: TabObj<T>, index: number) =>
                                <TabPanel key={index}>
                                    <div
                                        className='d-flex flex-column scroll-y me-n7 pe-7 tp-form-fields'
                                        id='kt_modal_add_client_scroll'
                                        data-kt-scroll='true'
                                        data-kt-scroll-activate='{default: false, lg: true}'
                                        data-kt-scroll-max-height='auto'
                                        data-kt-scroll-dependencies='#kt_modal_add_client_header'
                                        data-kt-scroll-wrappers='#kt_modal_add_client_scroll'
                                        data-kt-scroll-offset='300px'
                                    >
                                        <Row>
                                            <CreateForm<T> hasTabs tabName={tab.name} formik={formik} fields={tab.fields} entity={entity} actions={actions} />
                                        </Row>
                                    </div>
                                </TabPanel>
                            )}
                        </Tabs>
                        : <div
                            className='d-flex flex-column scroll-y me-n7 pe-7 tp-form-fields'
                            id='kt_modal_add_client_scroll'
                            data-kt-scroll='true'
                            data-kt-scroll-activate='{default: false, lg: true}'
                            data-kt-scroll-max-height='auto'
                            data-kt-scroll-dependencies='#kt_modal_add_client_header'
                            data-kt-scroll-wrappers='#kt_modal_add_client_scroll'
                            data-kt-scroll-offset='300px'
                        >
                            <Row>
                                <CreateForm<T> formik={formik} fields={formFields as FormFields<T>} entity={entity} actions={actions} />
                            </Row>
                        </div>
                }
            </>
        }
        ,
        validationRules: createYupSchemaFromFormFields<T>(flattenFieldsValidation(formFields)),
    }
}

export default FormBuilder;
